import {
  createForecastManualLine as createForecastManualLineApi,
  deleteForecastManualLine as deleteForecastManualLineApi,
  fetchReportingData,
  fetchReportingDashboard,
  fetchReportingFnp,
  updateForecastManualLine as updateForecastManualLineApi,
} from '@/apis/reporting';
import { fnpSortConfigToQueryParams } from '@/lib/reporting/fnpSort';
import { esnSortsToQueryParams } from '@/lib/reporting/esnBilledDaysSort';
import { reportingPeriodToQueryParams } from '@/lib/reporting/reportingPeriod';
import dayjs from 'dayjs';

const formatMonthParam = (date) => {
  if (!date) return undefined;
  // Handle both Date objects and dayjs objects
  if (date instanceof Date) {
    return dayjs(date).format('YYYY-MM');
  }
  if (dayjs.isDayjs(date)) {
    return date.format('YYYY-MM');
  }
  return undefined;
};

export const getReportingList = async (params = {}) => {
  const { page = 1, pageSize = 10, startMonth, endMonth, teamTagId, missionId, hasCirAccreditation, teamId } = params;

  const queryParams = {
    page,
    per_page: pageSize,
    team_id: teamId || undefined,
    start_month: formatMonthParam(startMonth),
    end_month: formatMonthParam(endMonth),
    team_tag_id: teamTagId || undefined,
    mission_id: missionId || undefined,
  };

  if (hasCirAccreditation !== null && hasCirAccreditation !== undefined && hasCirAccreditation !== 'all') {
    queryParams.has_cir_accreditation = hasCirAccreditation;
  }

  try {
    const response = await fetchReportingData(queryParams);
    const payload = response?.data || {};

    // Transform backend data structure to frontend format
    const items = (payload.items || []).map((item) => ({
      id: item.id,
      freelancerName: [item.freelancer_first_name, item.freelancer_last_name].filter(Boolean).join(' ') || '-',
      freelancerFirstName: item.freelancer_first_name || '',
      freelancerLastName: item.freelancer_last_name || '',
      freelancerId: item.freelancer_id, // ID du freelancer
      team: item.team_tag || '-', // Premier tag pour compatibilité
      teamTags: item.team_tags || [item.team_tag || '-'].filter(Boolean), // Tous les tags
      amountExclVAT: item.invoice_amount_excl_vat !== undefined ? `€${Number(item.invoice_amount_excl_vat).toFixed(2)}` : '-',
      amountExclVATNumber: item.invoice_amount_excl_vat || 0, // Pour les calculs
      amountInclVAT: item.invoice_amount_incl_vat !== undefined ? `€${Number(item.invoice_amount_incl_vat).toFixed(2)}` : '-',
      mission: item.mission_name || '-',
      missionId: item.mission_id ?? null,
      month: item.report_month || '-',
      projectCode: item.project_code ?? '-',
    }));

    return {
      success: true,
      data: {
        list: items,
        total: payload.total || items.length,
        pageSize: payload.per_page || pageSize,
        currentPage: payload.current_page || page,
      },
    };
  } catch (error) {
    console.error('Failed to fetch reporting data:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export const sendActivityReportReminder = async (freelancerId, missionId) => {
  try {
    const { default: service } = await import('@/utils/https');
    const response = await service.post('/api/app/v1/activity-report/remind', {
      freelancer_id: freelancerId,
      mission_id: missionId,
    });

    return {
      success: true,
      data: response.data?.data,
    };
  } catch (error) {
    console.error('Failed to send activity report reminder:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to send reminder',
    };
  }
};

export const getDashboard = async (period, teamId, esnSorts) => {
  try {
    const esnSortParams =
      esnSorts?.freelancer && esnSorts?.client
        ? esnSortsToQueryParams(esnSorts.freelancer, esnSorts.client)
        : {};
    const response = await fetchReportingDashboard({
      ...reportingPeriodToQueryParams(period),
      ...esnSortParams,
      team_id: teamId || undefined,
    });
    const payload = response?.data || response;

    return {
      success: true,
      data: payload,
    };
  } catch (error) {
    console.error('Failed to fetch reporting dashboard:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export const getFnp = async (period, teamId, staffingPercent, sortConfig, mode = 'fnp') => {
  try {
    const sortParams =
      sortConfig && typeof sortConfig === 'object'
        ? fnpSortConfigToQueryParams(sortConfig)
        : {};
    const response = await fetchReportingFnp({
      ...reportingPeriodToQueryParams(period),
      ...sortParams,
      team_id: teamId || undefined,
      mode: mode === 'forecast' ? 'forecast' : 'fnp',
      staffing_percent:
        typeof staffingPercent === 'number' && Number.isFinite(staffingPercent)
          ? staffingPercent
          : undefined,
    });
    const payload = response?.data || response;

    return {
      success: true,
      data: payload,
    };
  } catch (error) {
    console.error('Failed to fetch FNP reporting data:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export const createForecastManualLine = async (payload, teamId) => {
  try {
    const response = await createForecastManualLineApi({
      ...payload,
      team_id: teamId || undefined,
    });
    return {
      success: true,
      data: response?.data || response,
    };
  } catch (error) {
    console.error('Failed to create forecast manual line:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export const updateForecastManualLine = async (lineId, payload, teamId) => {
  try {
    const response = await updateForecastManualLineApi(
      lineId,
      payload,
      { team_id: teamId || undefined }
    );
    return {
      success: true,
      data: response?.data || response,
    };
  } catch (error) {
    console.error('Failed to update forecast manual line:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export const deleteForecastManualLine = async (lineId, teamId) => {
  try {
    const response = await deleteForecastManualLineApi(lineId, {
      team_id: teamId || undefined,
    });
    return {
      success: true,
      data: response?.data || response,
    };
  } catch (error) {
    console.error('Failed to delete forecast manual line:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export const reportingService = {
  getReportingList,
  getDashboard,
  getFnp,
  createForecastManualLine,
  updateForecastManualLine,
  deleteForecastManualLine,
  sendActivityReportReminder,
};
